import { usePageRefresh } from '../../../context/PageRefreshContext';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getNoticesList,
    deleteNotice,
    publishNotice,
    archiveNotice,
    resendNotice,
    getNoticeEnums,
    getNoticeStats,
} from '../../../redux/actions/SocietyPortal/NoticeAction/NoticeAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import Pagination from '../../../components/common/Pagination';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_NOTICES_ADD,
    SOCIETY_PORTAL_NOTICES_EDIT,
    SOCIETY_PORTAL_NOTICES_VIEW,
    SOCIETY_PORTAL_NOTICE_BOARD,
} from '../../../Routing/routes';
import {
    NOTICE_TYPE_STYLE,
    NOTICE_TYPE_ICON,
    STATUS_STYLE,
    PRIORITY_STYLE,
    CATEGORY_STYLE,
    CATEGORY_ICON,
    FALLBACK_ENUMS,
    AUDIENCE_LABEL,
    humanize,
    fmtDateTime,
    effectiveStatus,
    excerpt,
    authorName,
} from './noticeConstants';

const STAT_CARDS = [
    { key: 'PUBLISHED', label: 'Published', icon: 'mdi:check-circle-outline', bg: 'tw-bg-emerald-50', text: 'tw-text-emerald-700' },
    { key: 'DRAFT', label: 'Drafts', icon: 'mdi:file-document-edit-outline', bg: 'tw-bg-slate-100', text: 'tw-text-slate-700' },
    { key: 'SCHEDULED', label: 'Scheduled', icon: 'mdi:clock-outline', bg: 'tw-bg-indigo-50', text: 'tw-text-indigo-700' },
    { key: 'EXPIRED', label: 'Expired', icon: 'mdi:calendar-remove-outline', bg: 'tw-bg-amber-50', text: 'tw-text-amber-700' },
];

// Admin list for notices and circulars.
//
// One screen for both kinds. A circular is a notice with notice_type CIRCULAR:
// the tabs below filter by type and the add page asks which one to create, so
// there is no separate Circulars screen. `defaultType` is kept for a caller that
// which both preselects the tab and locks the type on the create button — that
// way both screens stay in sync with a single implementation.
const Notices = ({ defaultType = '' }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const {
        noticesListData,
        noticesListLoading,
        noticesPagination,
        deleteNoticeLoading,
        publishLoading,
        archiveLoading,
        resendLoading,
        noticeEnumsData,
        statsData,
        statsLoading,
    } = useSelector(state => state.NoticeReducer || {});

    const [noticeType, setNoticeType] = useState(defaultType);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [category, setCategory] = useState('');
    const [priority, setPriority] = useState('');
    const [page, setPage] = useState(1);
    const itemsPerPage = 10;

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [resendTarget, setResendTarget] = useState(null);
    const [viewMode, setViewMode] = useState('list');

    // Keep the tab honest when the route changes under us (Notices <-> Circulars
    // reuse this component, so the prop is the source of truth on mount).
    useEffect(() => {
        setNoticeType(defaultType);
        setPage(1);
    }, [defaultType]);

    const fetchList = useCallback(() => {
        if (!societyId) return;
        dispatch(getNoticesList({
            societyId,
            page,
            limit: itemsPerPage,
            search: search || undefined,
            noticeType: noticeType || undefined,
            status: status || undefined,
            category: category || undefined,
            priority: priority || undefined,
            includeExpired: true,
        }));
    }, [dispatch, societyId, page, search, noticeType, status, category, priority]);
    usePageRefresh(fetchList);

    useEffect(() => { fetchList(); }, [fetchList]);
    useEffect(() => {
        if (!noticeEnumsData) dispatch(getNoticeEnums());
    }, [dispatch, noticeEnumsData]);
    useEffect(() => {
        if (societyId) dispatch(getNoticeStats({ societyId, noticeType: noticeType || undefined }));
    }, [dispatch, societyId, noticeType, noticesListData]);

    const handleDelete = () => {
        if (!deleteTarget) return;
        dispatch(deleteNotice({
            id: deleteTarget.id,
            onSuccess: () => { setDeleteTarget(null); fetchList(); },
        }));
    };

    const handlePublish = (id) => {
        dispatch(publishNotice({ id, onSuccess: fetchList }));
    };

    const handleArchive = (id) => {
        dispatch(archiveNotice({ id, onSuccess: fetchList }));
    };

    const handleResend = () => {
        if (!resendTarget) return;
        dispatch(resendNotice({
            id: resendTarget.id,
            onSuccess: () => { setResendTarget(null); fetchList(); },
        }));
    };


    const rows = useMemo(
        () => (Array.isArray(noticesListData) ? noticesListData : []),
        [noticesListData]
    );
    const totalPages = noticesPagination?.totalPages || 1;
    const totalItems = noticesPagination?.total || rows.length;

    const statuses = noticeEnumsData?.statuses || FALLBACK_ENUMS.statuses;
    const categories = noticeEnumsData?.categories || FALLBACK_ENUMS.categories;
    const priorities = noticeEnumsData?.priorities || FALLBACK_ENUMS.priorities;

    const heading = 'Notices & Circulars';
    const subHeading = 'Announcements and formal circulars for your society';
    const addLabel = 'New Notice';
    const addPath = SOCIETY_PORTAL + SOCIETY_PORTAL_NOTICES_ADD;

    const TypeBadge = ({ value }) => (
        <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold ${NOTICE_TYPE_STYLE[value] || NOTICE_TYPE_STYLE.NOTICE}`}>
            <Icon icon={NOTICE_TYPE_ICON[value] || NOTICE_TYPE_ICON.NOTICE} className="tw-text-xs" />
            {humanize(value)}
        </span>
    );

    // All actions shown directly as inline icon buttons — no kebab/three-dot menu.
    const RowActions = ({ n }) => {
        const id = n.notice_id;
        const st = effectiveStatus(n);
        const isLive = st === 'PUBLISHED' || st === 'EXPIRED';

        return (
            <div className="tw-inline-flex tw-items-center tw-gap-0.5">
                {/* View */}
                <button
                    title="View"
                    onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_NOTICES_VIEW.replace(':id', id))}
                    className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-blue-600 tw-transition-colors"
                >
                    <Icon icon="mdi:eye-outline" className="tw-text-base" />
                </button>

                {/* Edit */}
                <button
                    title="Edit"
                    onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_NOTICES_EDIT.replace(':id', id))}
                    className="tw-p-1.5 tw-rounded hover:tw-bg-amber-50 tw-text-amber-500 tw-transition-colors"
                >
                    <Icon icon="mdi:pencil-outline" className="tw-text-base" />
                </button>

                {/* Acknowledgements */}
                <button
                    title={
                        n.requires_acknowledgement
                            ? `Acknowledgements (${n.acknowledgement_count ?? 0}/${n.target_count ?? '—'})`
                            : 'Acknowledgements not required'
                    }
                    disabled={!n.requires_acknowledgement}
                    onClick={() => navigate(`${SOCIETY_PORTAL}${SOCIETY_PORTAL_NOTICES_VIEW.replace(':id', id)}?panel=acknowledgements`)}
                    className="tw-p-1.5 tw-rounded hover:tw-bg-teal-50 tw-text-teal-500 tw-transition-colors disabled:tw-opacity-30 disabled:tw-cursor-not-allowed"
                >
                    <Icon icon="mdi:check-decagram-outline" className="tw-text-base" />
                </button>

                {/* Resend notification */}
                <button
                    title={
                        !isLive
                            ? 'Publish the notice before resending'
                            : !n.send_email
                                ? 'Email notification is switched off for this notice'
                                : `Resend notification${n.resend_count ? ` (sent ${n.resend_count}x)` : ''}`
                    }
                    disabled={!isLive || !n.send_email}
                    onClick={() => setResendTarget({ id, code: n.notice_code, title: n.title })}
                    className="tw-p-1.5 tw-rounded hover:tw-bg-sky-50 tw-text-sky-500 tw-transition-colors disabled:tw-opacity-30 disabled:tw-cursor-not-allowed"
                >
                    <Icon icon="mdi:email-sync-outline" className="tw-text-base" />
                </button>

                {/* Publish now — only for drafts */}
                {st === 'DRAFT' && (
                    <button
                        title="Publish now"
                        disabled={publishLoading}
                        onClick={() => handlePublish(id)}
                        className="tw-p-1.5 tw-rounded hover:tw-bg-emerald-50 tw-text-emerald-600 tw-transition-colors disabled:tw-opacity-30 disabled:tw-cursor-not-allowed"
                    >
                        <Icon icon="mdi:send-outline" className="tw-text-base" />
                    </button>
                )}

                {/* Archive — only for live/expired notices */}
                {isLive && (
                    <button
                        title="Archive"
                        disabled={archiveLoading}
                        onClick={() => handleArchive(id)}
                        className="tw-p-1.5 tw-rounded hover:tw-bg-orange-50 tw-text-orange-500 tw-transition-colors disabled:tw-opacity-30 disabled:tw-cursor-not-allowed"
                    >
                        <Icon icon="mdi:archive-arrow-down-outline" className="tw-text-base" />
                    </button>
                )}

                {/* Delete */}
                <button
                    title="Delete"
                    onClick={() => setDeleteTarget({ id, code: n.notice_code, title: n.title })}
                    className="tw-p-1.5 tw-rounded hover:tw-bg-rose-50 tw-text-rose-500 tw-transition-colors"
                >
                    <Icon icon="mdi:trash-can-outline" className="tw-text-base" />
                </button>
            </div>
        );
    };

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen tw-space-y-4">
            {/* Stats banner */}
            <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-3">
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-3 tw-flex tw-items-center tw-gap-3">
                    <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-gray-100 tw-text-gray-700 tw-flex tw-items-center tw-justify-center">
                        <Icon icon="mdi:bulletin-board" className="tw-text-lg" />
                    </div>
                    <div>
                        <p className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-500 tw-font-semibold">Total</p>
                        <p className="tw-text-lg tw-font-bold tw-text-gray-800 tw-leading-tight">
                            {statsLoading ? '—' : (statsData?.total ?? 0)}
                        </p>
                    </div>
                </div>
                {STAT_CARDS.map(card => (
                    <div key={card.key} className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-3 tw-flex tw-items-center tw-gap-3">
                        <div className={`tw-w-10 tw-h-10 tw-rounded-lg ${card.bg} ${card.text} tw-flex tw-items-center tw-justify-center`}>
                            <Icon icon={card.icon} className="tw-text-lg" />
                        </div>
                        <div>
                            <p className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-500 tw-font-semibold">{card.label}</p>
                            <p className="tw-text-lg tw-font-bold tw-text-gray-800 tw-leading-tight">
                                {statsLoading ? '—' : (statsData?.byStatus?.[card.key] ?? 0)}
                            </p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Pending acknowledgement callout */}
            {statsData?.awaitingAcknowledgement > 0 && (
                <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-rounded-lg tw-bg-amber-50 tw-border tw-border-amber-100 tw-text-amber-800 tw-text-xs">
                    <Icon icon="mdi:account-clock-outline" className="tw-text-base" />
                    <span>
                        <strong>{statsData.awaitingAcknowledgement}</strong> notice
                        {statsData.awaitingAcknowledgement === 1 ? ' is' : 's are'} still waiting on resident acknowledgement.
                    </span>
                </div>
            )}

            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <div>
                        <h1 className="tw-text-base tw-font-bold tw-text-gray-800">{heading}</h1>
                        <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">{subHeading}</p>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <div className="tw-inline-flex tw-p-0.5 tw-bg-gray-100 tw-rounded-md">
                            <button
                                type="button"
                                onClick={() => setViewMode('card')}
                                title="Card view"
                                className={`tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1.5 tw-rounded tw-text-xs tw-font-medium tw-transition-colors ${viewMode === 'card' ? 'tw-bg-white tw-text-gray-800 tw-shadow-sm' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}
                            >
                                <Icon icon="mdi:view-grid-outline" className="tw-text-base" />
                                Cards
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode('list')}
                                title="List view"
                                className={`tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1.5 tw-rounded tw-text-xs tw-font-medium tw-transition-colors ${viewMode === 'list' ? 'tw-bg-white tw-text-gray-800 tw-shadow-sm' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}
                            >
                                <Icon icon="mdi:format-list-bulleted" className="tw-text-base" />
                                List
                            </button>
                        </div>
                        <button
                            onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_NOTICE_BOARD)}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-700 tw-border tw-border-gray-200 tw-bg-white hover:tw-bg-gray-50"
                        >
                            <Icon icon="mdi:bulletin-board" className="tw-text-base" />
                            Notice Board
                        </button>
                        <button
                            onClick={() => navigate(addPath)}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700 tw-transition-colors"
                        >
                            <Icon icon="mdi:plus" className="tw-text-base" />
                            {addLabel}
                        </button>
                    </div>
                </div>

                {/* Notice / Circular tabs */}
                {!defaultType && (
                    <div className="tw-px-4 tw-pt-3 tw-flex tw-items-center tw-gap-1 tw-border-b tw-border-gray-100">
                        {[
                            { value: '', label: 'All' },
                            { value: 'NOTICE', label: 'Notices' },
                            { value: 'CIRCULAR', label: 'Circulars' },
                        ].map(tab => (
                            <button
                                key={tab.value || 'all'}
                                type="button"
                                onClick={() => { setNoticeType(tab.value); setPage(1); }}
                                className={`tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-border-b-2 tw-transition-colors ${noticeType === tab.value
                                    ? 'tw-border-sky-600 tw-text-sky-700'
                                    : 'tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700'
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                )}

                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-bg-gray-50/50 tw-grid tw-grid-cols-1 sm:tw-grid-cols-5 tw-gap-3">
                    <div className="sm:tw-col-span-2 tw-relative">
                        <Icon icon="mdi:magnify" className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by code, title, content..."
                            value={search}
                            onChange={e => { setSearch(e.target.value); setPage(1); }}
                            className="tw-w-full tw-pl-9 tw-pr-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-sky-500"
                        />
                    </div>
                    <select
                        value={status}
                        onChange={e => { setStatus(e.target.value); setPage(1); }}
                        className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-sky-500"
                    >
                        <option value="">All Status</option>
                        {statuses.map(s => <option key={s} value={s}>{humanize(s)}</option>)}
                    </select>
                    <select
                        value={category}
                        onChange={e => { setCategory(e.target.value); setPage(1); }}
                        className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-sky-500"
                    >
                        <option value="">All Categories</option>
                        {categories.map(c => <option key={c} value={c}>{humanize(c)}</option>)}
                    </select>
                    <select
                        value={priority}
                        onChange={e => { setPriority(e.target.value); setPage(1); }}
                        className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-sky-500"
                    >
                        <option value="">All Priorities</option>
                        {priorities.map(p => <option key={p} value={p}>{humanize(p)}</option>)}
                    </select>
                </div>

                {viewMode === 'list' ? (
                    <div className="tw-overflow-x-auto">
                        <table className="tw-w-full tw-text-xs">
                            <thead>
                                <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                    <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Code</th>
                                    <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Title</th>
                                    <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Type</th>
                                    <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Category</th>
                                    <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Priority</th>
                                    <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Status</th>
                                    <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Audience</th>
                                    <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Published</th>
                                    <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]" title="Residents who confirmed they read this notice">Read</th>
                                    <th className="tw-text-right tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {noticesListLoading && (
                                    <tr>
                                        <td colSpan={10} className="tw-text-center tw-py-12">
                                            <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                                <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-sky-500"></div>
                                                <span className="tw-text-xs tw-text-gray-400">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                {!noticesListLoading && rows.length === 0 && (
                                    <tr>
                                        <td colSpan={10} className="tw-text-center tw-py-12 tw-text-xs tw-text-gray-400">
                                            No notices found
                                        </td>
                                    </tr>
                                )}
                                {!noticesListLoading && rows.map((n) => {
                                    const st = effectiveStatus(n);
                                    return (
                                        <tr key={n.notice_id} className="tw-border-b tw-border-gray-50 hover:tw-bg-slate-50/50 tw-transition-colors">
                                            <td className="tw-px-4 tw-py-2.5 tw-text-gray-800 tw-font-mono tw-font-semibold">{n.notice_code || '—'}</td>
                                            <td className="tw-px-4 tw-py-2.5 tw-text-gray-800 tw-max-w-xs">
                                                <div className="tw-flex tw-items-center tw-gap-1.5">
                                                    {n.is_pinned && <Icon icon="mdi:pin" className="tw-text-amber-500 tw-shrink-0" title="Pinned" />}
                                                    <span className="tw-truncate" title={n.title}>{n.title}</span>
                                                </div>
                                            </td>
                                            <td className="tw-px-4 tw-py-2.5"><TypeBadge value={n.notice_type} /></td>
                                            <td className="tw-px-4 tw-py-2.5">
                                                <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold ${CATEGORY_STYLE[n.category] || CATEGORY_STYLE.GENERAL}`}>
                                                    <Icon icon={CATEGORY_ICON[n.category] || CATEGORY_ICON.GENERAL} className="tw-text-xs" />
                                                    {humanize(n.category)}
                                                </span>
                                            </td>
                                            <td className="tw-px-4 tw-py-2.5">
                                                <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${PRIORITY_STYLE[n.priority] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                                    {humanize(n.priority)}
                                                </span>
                                            </td>
                                            <td className="tw-px-4 tw-py-2.5">
                                                <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${STATUS_STYLE[st] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                                    {humanize(st)}
                                                </span>
                                            </td>
                                            <td className="tw-px-4 tw-py-2.5 tw-text-gray-700">{AUDIENCE_LABEL[n.audience] || humanize(n.audience)}</td>
                                            <td className="tw-px-4 tw-py-2.5 tw-text-gray-700">
                                                {n.published_at ? fmtDateTime(n.published_at) : (n.publish_at ? `Scheduled ${fmtDateTime(n.publish_at)}` : '—')}
                                            </td>
                                            <td className="tw-px-4 tw-py-2.5 tw-text-gray-700">
                                                {n.requires_acknowledgement
                                                    ? `${n.acknowledgement_count ?? 0}/${n.target_count ?? '—'}`
                                                    : <span className="tw-text-gray-300">—</span>}
                                            </td>
                                            <td className="tw-px-4 tw-py-2.5 tw-text-right"><RowActions n={n} /></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="tw-p-4">
                        {noticesListLoading && (
                            <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-12">
                                <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-sky-500"></div>
                                <span className="tw-text-xs tw-text-gray-400">Loading...</span>
                            </div>
                        )}
                        {!noticesListLoading && rows.length === 0 && (
                            <div className="tw-text-center tw-py-12 tw-text-xs tw-text-gray-400">No notices found</div>
                        )}
                        {!noticesListLoading && rows.length > 0 && (
                            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-3">
                                {rows.map(n => {
                                    const st = effectiveStatus(n);
                                    return (
                                        <div key={n.notice_id} className="tw-border tw-border-gray-100 tw-rounded-lg tw-p-3 tw-space-y-2 hover:tw-shadow-sm tw-transition-shadow">
                                            <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                                                <span className="tw-font-mono tw-text-[10px] tw-font-semibold tw-text-gray-500">{n.notice_code || '—'}</span>
                                                <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${STATUS_STYLE[st] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                                    {humanize(st)}
                                                </span>
                                            </div>
                                            <div className="tw-flex tw-items-center tw-gap-1.5">
                                                {n.is_pinned && <Icon icon="mdi:pin" className="tw-text-amber-500 tw-shrink-0" />}
                                                <h3 className="tw-text-xs tw-font-bold tw-text-gray-800 tw-truncate" title={n.title}>{n.title}</h3>
                                            </div>
                                            <p className="tw-text-[11px] tw-text-gray-500 tw-line-clamp-2">
                                                {n.summary || excerpt(n.body, 110)}
                                            </p>
                                            <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                                                <TypeBadge value={n.notice_type} />
                                                <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${PRIORITY_STYLE[n.priority] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                                    {humanize(n.priority)}
                                                </span>
                                            </div>
                                            <div className="tw-flex tw-items-center tw-justify-between tw-text-[10px] tw-text-gray-500 tw-pt-1 tw-border-t tw-border-gray-50">
                                                <span className="tw-truncate">{authorName(n.createdBy)}</span>
                                                <span>{n.published_at ? fmtDateTime(n.published_at) : '—'}</span>
                                            </div>
                                            <div className="tw-flex tw-items-center tw-justify-end tw-pt-1">
                                                <RowActions n={n} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {!noticesListLoading && rows.length > 0 && totalPages > 1 && (
                    <div className="tw-p-4 tw-border-t tw-border-gray-100">
                        <Pagination
                            currentPage={page}
                            totalPages={totalPages}
                            totalItems={totalItems}
                            itemsPerPage={itemsPerPage}
                            onPageChange={setPage}
                            showItemsPerPage={false}
                            itemLabel="notices"
                        />
                    </div>
                )}
            </div>

            {resendTarget && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                    <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-sm tw-overflow-hidden">
                        <div className="tw-p-4 tw-border-b tw-border-gray-100">
                            <h3 className="tw-text-sm tw-font-bold tw-text-gray-800">Resend this notice?</h3>
                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                                Everyone in its audience receives the email again.
                            </p>
                        </div>
                        <div className="tw-p-5">
                            <p className="tw-text-xs tw-text-gray-600 tw-mb-4">
                                <span className="tw-font-semibold">{resendTarget.code}</span> — {resendTarget.title}
                            </p>
                            <div className="tw-flex tw-justify-end tw-gap-2">
                                <button onClick={() => setResendTarget(null)} className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50">Cancel</button>
                                <button onClick={handleResend} disabled={resendLoading} className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700 tw-rounded-md disabled:tw-opacity-50">
                                    <Icon icon="mdi:email-sync-outline" className="tw-text-base" />
                                    {resendLoading ? 'Sending...' : 'Resend'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {deleteTarget && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                    <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-sm tw-overflow-hidden">
                        <div className="tw-p-4 tw-border-b tw-border-gray-100">
                            <h3 className="tw-text-sm tw-font-bold tw-text-gray-800">Delete Notice?</h3>
                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">This action cannot be undone.</p>
                        </div>
                        <div className="tw-p-5">
                            <p className="tw-text-xs tw-text-gray-600 tw-mb-4">
                                You are about to delete <span className="tw-font-semibold">{deleteTarget.code || 'this notice'}</span> — {deleteTarget.title}.
                            </p>
                            <div className="tw-flex tw-justify-end tw-gap-2">
                                <button onClick={() => setDeleteTarget(null)} className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50">Cancel</button>
                                <button onClick={handleDelete} disabled={deleteNoticeLoading} className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-rounded-md disabled:tw-opacity-50">
                                    {deleteNoticeLoading ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Notices;
